import { BlankModal, ModalBody, ModalHeader, InlineBanner } from '@cad-arc';
import { useCallback } from 'react';
import { LoadingIndicator } from '../common/components/loading-indicator';
import { UnitStateData } from '@mark43/cad-api';
import { useFullscreenModalControls } from '../modals/context/modal-context-provider';
import { useEventPanelViewQuery } from '../event-panel/event-state/use-event-panel-view-query';
import { useTransitionUnitStatusMutation } from '../unit-status/use-transition-unit-status-mutation';
import { useEnRouteStates } from '../common/hooks/use-en-route-states';
import { AssignmentModalDetails } from './components/assignment-modal-details';
import { IncomingAssignmentModalFooter } from './footer/incoming-assignment-modal-footer';
import { IncomingAssignmentModalFooterAed } from './footer/incoming-assignment-modal-footer-aed';
import { WorkspacePanelProvider } from '../panels/workspace-panel-provider';
import { useEventQueueWorkspacePanel } from '../common/hooks/use-event-queue-workspace-panel';
import { colors } from '../colors/colors';
import { useIncomingAssignmentAlert } from './use-incoming-assignment-alert';

export type IncomingAssignmentModalComponentType = typeof IncomingAssignmentModal;

export function IncomingAssignmentModal(props: {
    eventId: number;
    isIncomingEventAed?: boolean;
}): React.ReactElement {
    const workspacePanel = useEventQueueWorkspacePanel();
    const eventQuery = useEventPanelViewQuery({ eventId: props.eventId });
    const modalControls = useFullscreenModalControls();
    const transitionUnitStatusMutation = useTransitionUnitStatusMutation();
    const { enRouteState, alternateEnRouteState } = useEnRouteStates(props.isIncomingEventAed);
    const stopIncomingAssignmentAlert = useIncomingAssignmentAlert({
        eventId: props.eventId,
        isAed: !!props.isIncomingEventAed,
    });

    const errorMessage = ((transitionUnitStatusMutation.error as unknown) as any)?.response?.data
        ?.error;

    const headerColorStyle = props.isIncomingEventAed
        ? { backgroundColor: colors.priorityOneColor }
        : undefined;
    const headerTitle = props.isIncomingEventAed
        ? 'AUTO-DISPATCH ASSIGNMENT'
        : 'Incoming Assignment';

    const transitionMutation = useCallback(
        (unitStateData?: UnitStateData) => {
            if (unitStateData) {
                transitionUnitStatusMutation
                    .mutateAsync({
                        unitStateData,
                    })
                    .then((transitioned) => {
                        if (transitioned) {
                            stopIncomingAssignmentAlert();
                        }
                    });
            }
        },
        [transitionUnitStatusMutation, stopIncomingAssignmentAlert]
    );

    const alternateEnRouteStateMutation = useCallback(() => {
        transitionMutation(alternateEnRouteState);
    }, [alternateEnRouteState, transitionMutation]);

    const enRouteStateMutation = useCallback(() => {
        transitionMutation(enRouteState);
    }, [enRouteState, transitionMutation]);

    const commonFooterProps = {
        alternateEnRouteStateMutation,
        alternateEnRouteState,
        mutationIsLoading: transitionUnitStatusMutation?.isLoading,
        enRouteStateMutation,
        enRouteState,
        modalControls,
    };

    return (
        <BlankModal
            isOpen
            onClose={() => modalControls.close()}
            size="lg"
            style={{ maxHeight: 535 }}
            scrollBehavior="inside"
            closeOnOverlayClick={false}
            closeOnEsc={false}
            hideCloseButton
        >
            <ModalHeader style={headerColorStyle}>{headerTitle}</ModalHeader>

            <ModalBody>
                {!!errorMessage && <InlineBanner title={errorMessage} status="error" />}
                {!eventQuery.data ? (
                    <LoadingIndicator />
                ) : (
                    <AssignmentModalDetails eventData={eventQuery.data} />
                )}
            </ModalBody>

            {props.isIncomingEventAed ? (
                <WorkspacePanelProvider workspacePanel={workspacePanel}>
                    <IncomingAssignmentModalFooterAed
                        {...commonFooterProps}
                        eventId={props.eventId}
                    />
                </WorkspacePanelProvider>
            ) : (
                <IncomingAssignmentModalFooter {...commonFooterProps} />
            )}
        </BlankModal>
    );
}
